import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import ModalContext from 'contexts/ModalContext'
import TravellersModal from '../shared/travellers/Modal'
import RoomsModal from '../shared/rooms/Modal'
import { useMultiRoomSelectionOfferPage } from 'businessTraveller/hooks/useMultiRoomSelection'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

function BusinessTravellerSelectHotelsOfferPageMobile() {
  const {
    totalTravellers,
    roomCount,
  } = useMultiRoomSelectionOfferPage()

  const showModal = useContext(ModalContext)
  const { checkInDate, activeFilter } = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()

  // Use refs so the modal onApply callbacks always read the latest values
  const totalTravellersRef = useRef(totalTravellers)
  totalTravellersRef.current = totalTravellers
  const checkInDateRef = useRef(checkInDate)
  checkInDateRef.current = checkInDate

  const openCalendarIfNoDates = useCallback(() => {
    if (!checkInDateRef.current) {
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, [offerPageDispatch])

  const showRoomsModal = useCallback(async() => {
    await showModal(<RoomsModal onApply={openCalendarIfNoDates} useSelection={useMultiRoomSelectionOfferPage} />)
  }, [showModal, openCalendarIfNoDates])

  const showTravellersModal = useCallback(async() => {
    await showModal(<TravellersModal
      onApply={() => {
        if (totalTravellersRef.current > 1) {
          showRoomsModal()
        } else {
          openCalendarIfNoDates()
        }
      }}
      useSelection={useMultiRoomSelectionOfferPage}
    />)
  }, [showModal, showRoomsModal, openCalendarIfNoDates])

  const mobileContainerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (activeFilter === 'multiRoomTravellers' && mobileContainerRef.current?.offsetParent !== null) {
      showTravellersModal()
      offerPageDispatch({ type: OfferPageActions.closeFilter })
    }
  }, [activeFilter, showTravellersModal, offerPageDispatch])

  return (
    <div ref={mobileContainerRef} style={{ display: 'flex', gap: 8 }}>
      <AssistChip
        data-testid="multi-room-travellers-select"
        variant="outline"
        size="medium"
        startIcon={<LineUsersAltIcon />}
        endIcon={<LineAngleDownIcon />}
        onClick={showTravellersModal}
      >
        {totalTravellers > 0 ? pluralizeToString('adult', totalTravellers) : 'Add travellers'}
      </AssistChip>
      <AssistChip
        data-testid="multi-room-rooms-select"
        variant="outline"
        size="medium"
        startIcon={<LineBedIcon />}
        endIcon={<LineAngleDownIcon />}
        onClick={showRoomsModal}
      >
        {pluralizeToString('room', roomCount)}
      </AssistChip>
    </div>
  )
}

export default BusinessTravellerSelectHotelsOfferPageMobile
